/**
 * Where to continue after login or registration.
 * Lives in sessionStorage so an abandoned checkout does not survive the next visit.
 */

export const REDIRECT_STORAGE_KEY = 'redirectAfterLogin';

function remember(url: string): void {
  sessionStorage.setItem(REDIRECT_STORAGE_KEY, url);
  localStorage.removeItem(REDIRECT_STORAGE_KEY);
}

export const saveRedirectUrl = (url?: string): void => {
  const redirectUrl = url || window.location.pathname + window.location.search;
  remember(redirectUrl);
};

export const getRedirectUrl = (): string | null => {
  localStorage.removeItem(REDIRECT_STORAGE_KEY);
  return sessionStorage.getItem(REDIRECT_STORAGE_KEY);
};

export const clearRedirectUrl = (): void => {
  sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
  localStorage.removeItem(REDIRECT_STORAGE_KEY);
};

/** Read the saved destination and forget it, so it cannot fire twice. */
export const consumeRedirectUrl = (): string | null => {
  const redirectUrl = sessionStorage.getItem(REDIRECT_STORAGE_KEY);
  clearRedirectUrl();
  return redirectUrl;
};

export const navigateToRedirectUrl = (navigate: (path: string) => void, defaultPath: string = '/'): void => {
  const redirectUrl = consumeRedirectUrl();
  navigate(redirectUrl || defaultPath);
};

/** A saved checkout for a plugin this account already owns opens the owned page instead. */
export async function destinationAfterLogin(token: string, redirectUrl: string): Promise<string> {
  const match = redirectUrl.match(/^\/checkout\/([^/?#]+)/);
  if (!match) return redirectUrl;
  const slug = decodeURIComponent(match[1]);
  try {
    const [pluginResponse, ownedResponse] = await Promise.all([
      fetch(`/api/plugins/${encodeURIComponent(slug)}`),
      fetch('/api/account/entitlements', { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (!pluginResponse.ok || !ownedResponse.ok) return redirectUrl;
    const plugin = await pluginResponse.json();
    const owned = await ownedResponse.json();
    const productId = plugin?.data?.id ? String(plugin.data.id) : '';
    if (!productId) return redirectUrl;
    const owns = (owned.entitlements || []).some(
      (item: { subjectType?: string; subjectId?: string }) =>
        item.subjectType === 'product' && String(item.subjectId) === productId,
    );
    if (!owns) return redirectUrl;
    return `/purchase/already-owned?slug=${encodeURIComponent(slug)}`;
  } catch {
    return redirectUrl;
  }
}
